import { Badge, Box, Group, Text, ThemeIcon, UnstyledButton, useMantineTheme } from "@mantine/core";
import { useLiveQuery } from "dexie-react-hooks";
import { type ChatEntity, db } from "../db";
import { type ChatBadgeStatus, useChatBackgroundBadge } from "../hooks/useChatCompletion";

const STATUS_BADGE: Record<
    Exclude<ChatBadgeStatus, undefined>,
    { label: string; color: string }
> = {
    running: { label: "Running", color: "blue" },
    ready: { label: "Ready", color: "green" },
    failed: { label: "Failed", color: "red" },
};

interface MainLinkProps {
    icon: React.ReactNode;
    chat: ChatEntity;
    isHighlighted: boolean;
}

export function MainLink({ icon, chat, isHighlighted }: Readonly<MainLinkProps>) {
    const theme = useMantineTheme();
    const color = isHighlighted ? theme.colors.purple[4] : "black";
    const variant = isHighlighted ? "dark" : "subtle";
    const textColor = isHighlighted ? "white" : "black";
    // "running" is live; "ready" / "failed" last until the chat is opened (session-only).
    const status = useChatBackgroundBadge(chat.id);
    const statusBadge = status ? STATUS_BADGE[status] : undefined;
    const firstMessage = useLiveQuery(async () => {
        return (await db.messages.orderBy("createdAt").toArray()).filter(
            (m) => m.chatId === chat.id,
        )[0];
    }, [chat]);

    return (
        <UnstyledButton
            style={(theme) => ({
                // display: "block",
                width: "100%",
                padding: theme.spacing.xs,
                borderRadius: theme.radius.sm,
                color,
            })}
        >
            <Group wrap="nowrap">
                <ThemeIcon variant={variant} color={color}>
                    {icon}
                </ThemeIcon>
                <Box
                    style={{
                        textOverflow: "ellipsis",
                        color: textColor,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        flex: 1,
                        width: 0,
                    }}
                >
                    <Text size="xs">
                        <strong>{firstMessage?.content}</strong>
                    </Text>

                    <Text size="xs">
                        {typeof firstMessage?.createdAt === "number"
                            ? new Intl.RelativeTimeFormat("en", { numeric: "auto" }).format(
                                  Math.round(
                                      (firstMessage.createdAt - Date.now()) / (1000 * 60 * 60 * 24),
                                  ),
                                  "day",
                              )
                            : typeof firstMessage?.createdAt === "object"
                              ? new Intl.RelativeTimeFormat("en", { numeric: "auto" }).format(
                                    Math.round(
                                        // @ts-expect-error
                                        (firstMessage.createdAt?.getTime() - Date.now()) /
                                            (1000 * 60 * 60 * 24),
                                    ),
                                    "day",
                                )
                              : firstMessage?.createdAt}
                    </Text>
                </Box>
                {statusBadge && (
                    <Badge
                        size="xs"
                        color={statusBadge.color}
                        variant="filled"
                        style={{ flexShrink: 0 }}
                        aria-label={`Background generation ${statusBadge.label}`}
                    >
                        {statusBadge.label}
                    </Badge>
                )}
            </Group>
        </UnstyledButton>
    );
}
